import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../hooks/useUsers";
import { useCompanies } from "../hooks/useCompanies";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  FeatherArrowLeft,
  FeatherUser,
  FeatherMail,
  FeatherLock,
} from "@subframe/core";
import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";

export const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { createUser, loading, error } = useUsers();
  const { companies } = useCompanies();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    userName: "",
    company_ids: [] as number[],
    isClient: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<
    Array<{ id: number; company_name: string }>
  >([]);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/companies/list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ page: 1, limit: 1000 }),
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCompanies(data.data?.companies || []);
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    
    // Clear API error when user starts typing
    if (apiError) {
      setApiError(null);
    }
  };

  const handleCompanyToggle = (companyId: number) => {
    setFormData((prev) => ({
      ...prev,
      company_ids: prev.company_ids.includes(companyId)
        ? prev.company_ids.filter((id) => id !== companyId)
        : [...prev.company_ids, companyId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setApiError(null);
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.userName.trim()) {
      newErrors.userName = "Username is required";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    if (formData.company_ids.length === 0) {
      newErrors.company_ids = "At least one company must be selected";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // If client is checked, set role_name to "client_view" for all selected companies
      const roles: { [companyId: number]: string } = {};
      if (formData.isClient) {
        formData.company_ids.forEach((companyId) => {
          roles[companyId] = "client_view";
        });
      }

      await createUser({
        email: formData.email,
        password: formData.password,
        userName: formData.userName,
        company_ids: formData.company_ids,
        roles: Object.keys(roles).length > 0 ? roles : undefined,
      });
      navigate("/admin/dashboard?tab=users");
    } catch (err: any) {
      // Extract error message from API response
      let errorMessage = "Failed to create user";
      
      if (err instanceof Error) {
        // Check if it's an APIError with data property
        if ((err as any).data) {
          const errorData = (err as any).data;
          errorMessage = errorData.message || err.message || errorMessage;
          
          // Handle field-level validation errors if present
          if (errorData.errors && typeof errorData.errors === 'object') {
            const fieldErrors: Record<string, string> = {};
            Object.keys(errorData.errors).forEach((key) => {
              const fieldError = errorData.errors[key];
              if (Array.isArray(fieldError)) {
                fieldErrors[key] = fieldError[0];
              } else if (typeof fieldError === 'string') {
                fieldErrors[key] = fieldError;
              }
            });
            if (Object.keys(fieldErrors).length > 0) {
              setErrors(fieldErrors);
            }
          }
        } else {
          errorMessage = err.message || errorMessage;
        }
      }
      
      setApiError(errorMessage);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col gap-6 p-12">
        <div className="flex w-full items-center gap-4">
          <Button
            variant="neutral-secondary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/admin/dashboard?tab=users")}
          >
            Back
          </Button>
          <span className="text-heading-2 font-heading-2 text-default-font">
            Create User
          </span>
        </div>

        <div className="flex w-full flex-col items-center justify-center">
          {(error || apiError) && (
            <div className="flex w-full max-w-2xl flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
              <span className="text-sm font-medium text-red-800">
                {apiError || error?.message || "An error occurred"}
              </span>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="flex w-full max-w-2xl flex-col items-start gap-6"
          >
            <div className="flex w-full flex-col gap-4">
              <TextField
                label="Email"
                helpText={errors.email || ""}
                error={!!errors.email}
                icon={<FeatherMail />}
              >
                <TextField.Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  disabled={loading}
                />
              </TextField>

              <TextField
                label="Username"
                helpText={errors.userName || ""}
                error={!!errors.userName}
                icon={<FeatherUser />}
              >
                <TextField.Input
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  placeholder="Enter username"
                  disabled={loading}
                />
              </TextField>

              <TextField
                label="Password"
                helpText={errors.password || "Minimum 8 characters"}
                error={!!errors.password}
                icon={<FeatherLock />}
              >
                <TextField.Input
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  disabled={loading}
                />
              </TextField>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isClient"
                  id="isClient"
                  checked={formData.isClient}
                  onChange={handleChange}
                  className="h-4 w-4"
                  disabled={loading}
                />
                <label
                  htmlFor="isClient"
                  className="text-body font-body text-default-font cursor-pointer"
                >
                  Is Client
                </label>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-caption-bold font-caption-bold text-default-font text-left">
                  Assign to Companies
                  {errors.company_ids && (
                    <span className="text-error-700 ml-2">
                      {errors.company_ids}
                    </span>
                  )}
                </label>
                <div className="flex flex-col gap-2 max-h-64 overflow-y-auto border border-solid border-neutral-border rounded-md p-4">
                  {availableCompanies.map((company) => (
                    <label
                      key={company.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.company_ids.includes(company.id)}
                        onChange={() => handleCompanyToggle(company.id)}
                        className="h-4 w-4"
                        disabled={loading}
                      />
                      <span className="text-body font-body text-default-font">
                        {company.company_name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  className="w-auto"
                >
                  Create User
                </Button>
                <Button
                  type="button"
                  variant="neutral-secondary"
                  onClick={() => navigate("/admin/dashboard?tab=users")}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
